import { useCallback, useMemo, useState } from 'react';
import { debounce, DebounceTimeoutValues } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { TextInput } from '../../../TextInput';
import { useTableContext } from '../../TableContext';
import { tableStateReducer } from '../../tableStateReducer';
import { ReducerActionType, TableFilters } from '../../types';

interface TableHeaderSearchProps {
    onRowSelectionChange?: React.Dispatch<React.SetStateAction<{}>>;
    placeholder?: React.ReactNode;
}

const TableHeaderSearch = <T, F extends TableFilters<T>>({
    onRowSelectionChange,
    placeholder,
}: TableHeaderSearchProps) => {
    const { state, onStateChange } = useTableContext<T, F>();
    const [globalSearchValue, setGlobalSearchValue] = useState(state?.globalFilters?.search || '');

    const { t } = useLocalization();

    const setGlobalFilter = useCallback(
        (value: string) => {
            const nextState = tableStateReducer(state, {
                type: ReducerActionType.GlobalSearch,
                payload: { search: value },
            });

            onStateChange(nextState);
            onRowSelectionChange?.({});
        },
        [onRowSelectionChange, onStateChange, state]
    );

    const debouncedSetGlobalFilter = useMemo(
        () => debounce(setGlobalFilter, DebounceTimeoutValues.textInput),
        [setGlobalFilter]
    );

    return (
        <TextInput
            clearable
            inline
            indicativeIcon={IconName.Search}
            aria-label="globalSearch"
            name="globalSearch"
            onChange={(event) => {
                setGlobalSearchValue((event.target as HTMLInputElement).value);
                debouncedSetGlobalFilter((event.target as HTMLInputElement).value);
            }}
            onClear={() => {
                setGlobalSearchValue('');
                debouncedSetGlobalFilter('');
            }}
            placeholder={placeholder || t(OptimusTextKey.TableHeaderSearch)}
            value={globalSearchValue}
            size="small"
        />
    );
};

export default TableHeaderSearch;
